import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { PageResponse } from '@models/api';
import type { GetPartnerProductsParams, PartnerProductResponse } from '@apis/product';
import { getPartnerProducts } from '@apis/product';

import { dropEmptyValues } from '../../utils/common';

const PAGE_SIZE = 50;

/**
 * Partner product list query.
 *
 * Key policy:
 * - queryKey MUST include both page and cleaned search params
 *   so that results update immediately when filters change.
 */
export const usePartnerProductList = ({
  page,
  searchParam,
}: {
  page: number;
  searchParam: GetPartnerProductsParams;
}) => {
  const cleanedSearchParam = useMemo(
    () =>
      dropEmptyValues(searchParam, [
        'productCode',
        'productName',
        'countryId',
      ]),
    [searchParam],
  );

  return useQuery<PageResponse<PartnerProductResponse>, Error>({
    queryKey: ['partnerProducts', page, cleanedSearchParam],
    queryFn: () =>
      getPartnerProducts({
        pageNo: page,
        pageSize: PAGE_SIZE,
        ...cleanedSearchParam,
      }),
    enabled: Number.isFinite(page) && page > 0,
    refetchOnMount: 'always',
  });
};
